import { Autocomplete, Grid, Paper, TextField } from '@mui/material'
import React from 'react'

import { DeskFormData } from '../../types/ComponentProps'
import Database from '../../types/Database'

interface DeskFormFieldsProps {
  formData: DeskFormData
  isEditMode: boolean
  availableOffices: Database.Office[]
  onChange: (field: keyof DeskFormData, value: string) => void
  preSelectedOffice?: Database.Office | null
}

/**
 * DeskFormFields - Component for desk form input fields
 *
 * Handles all form field rendering including desk name, office selection,
 * labels, and access restrictions.
 */
export default function DeskFormFields({
  formData,
  isEditMode,
  availableOffices,
  onChange,
  preSelectedOffice
}: DeskFormFieldsProps) {
  const handleFieldChange =
    (field: keyof DeskFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(field, event.target.value)
    }

  const handleOfficeNameChange = (
    _event: React.SyntheticEvent,
    value: string | null | undefined
  ) => {
    onChange('officeName', value || '')
  }

  const fieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }
    }
  }

  return (
    <Grid container spacing={3}>
      {/* Desk Name Field */}
      <Grid item xs={12} sm={6}>
        <TextField
          autoComplete='desk-name'
          value={formData.name}
          onChange={handleFieldChange('name')}
          name='deskName'
          required
          fullWidth
          id='deskName'
          label='Desk Name'
          autoFocus
          sx={fieldStyles}
        />
      </Grid>

      {/* Office Name Field - Only show in create mode */}
      {!isEditMode && (
        <Grid item xs={12} sm={6}>
          <Autocomplete
            id='officeName'
            freeSolo
            value={formData.officeName}
            onChange={handleOfficeNameChange}
            options={availableOffices.map(office => office.name)}
            disabled={!!preSelectedOffice}
            PaperComponent={props => (
              <Paper
                {...props}
                sx={{
                  width: 300,
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
                }}
              />
            )}
            renderInput={params => (
              <TextField
                {...params}
                label={
                  preSelectedOffice
                    ? 'Office Name (Pre-selected)'
                    : 'Office Name *'
                }
                sx={fieldStyles}
                helperText={
                  preSelectedOffice
                    ? `Creating desk in: ${preSelectedOffice.name}`
                    : undefined
                }
              />
            )}
          />
        </Grid>
      )}

      {/* Labels Field */}
      <Grid item xs={12}>
        <TextField
          required
          fullWidth
          id='labels'
          label='Labels (comma separated)'
          name='labels'
          value={formData.labels}
          onChange={handleFieldChange('labels')}
          autoComplete='labels'
          helperText='Enter labels separated by commas (e.g., quiet, window, standing)'
          sx={fieldStyles}
        />
      </Grid>

      {/* Available To Field */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          id='availableTo'
          label='Restrict Access (optional)'
          value={formData.availableTo}
          onChange={handleFieldChange('availableTo')}
          name='availableTo'
          autoComplete='availableTo'
          helperText='Enter email addresses separated by commas to restrict access'
          sx={fieldStyles}
        />
      </Grid>
    </Grid>
  )
}
